import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Header, Icon } from "semantic-ui-react";
import { useTour } from "@modules/GuidedTour/useTour";
import { TOUR_TYPES } from "@modules/GuidedTour/tourConfigs";
import { WelcomeManager } from "@utils/WelcomeGuideUtils";

const WelcomeModal = ({
  user,
  isAuthenticated,
  showWelcomeModal,
  setShowWelcomeModal,
}) => {
  const { startTour } = useTour();
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasError, setHasError] = useState(false);

  const timerRef = useRef(null);
  const tourTimerRef = useRef(null);

  // Component logic - runs regardless of render conditions
  useEffect(() => {
    if (!isAuthenticated || !user || !setShowWelcomeModal) return;

    try {
      if (
        !WelcomeManager ||
        typeof WelcomeManager.shouldShowWelcomeOnDashboard !== "function"
      ) {
        setHasError(true);
        return;
      }

      const shouldShow = WelcomeManager.shouldShowWelcomeOnDashboard(user);

      if (shouldShow) {
        timerRef.current = setTimeout(() => {
          if (timerRef.current && setShowWelcomeModal) {
            setShowWelcomeModal(true);
          }
        }, 1500);
      } else {
        setShowWelcomeModal(false);
      }
    } catch (error) {
      setHasError(true);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isAuthenticated, user, setShowWelcomeModal]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (tourTimerRef.current) {
        clearTimeout(tourTimerRef.current);
      }
    };
  }, []);

  const handleStartTour = (tourType) => {
    setIsProcessing(true);

    try {
      if (
        WelcomeManager &&
        typeof WelcomeManager.markWelcomeAsSeen === "function"
      ) {
        WelcomeManager.markWelcomeAsSeen(user);
      }
    } catch (error) {
      // Silent error handling
    }

    if (setShowWelcomeModal) {
      setShowWelcomeModal(false);
    }
    setIsProcessing(false);

    tourTimerRef.current = setTimeout(() => {
      if (tourTimerRef.current) {
        startTour(tourType);
        tourTimerRef.current = null;
      }
    }, 300);
  };

  const handleSkipTour = () => {
    setIsProcessing(true);

    try {
      if (
        WelcomeManager &&
        typeof WelcomeManager.markWelcomeAsSeen === "function"
      ) {
        WelcomeManager.markWelcomeAsSeen(user);
      }
    } catch (error) {
      // Silent error handling
    }

    if (setShowWelcomeModal) {
      setShowWelcomeModal(false);
    }
    setIsProcessing(false);
  };

  const shouldRenderModal =
    isAuthenticated && user && !hasError && WelcomeManager;

  return (
    <>
      {shouldRenderModal && (
        <Modal
          open={showWelcomeModal || false}
          size="small"
          closeOnDimmerClick={false}
          closeOnEscape={false}
        >
          <Header icon color="green">
            <Icon name="book" />
            Welcome to RSU Learning Resource Center-LIMS!
          </Header>

          <Modal.Content>
            <div style={{ textAlign: "center", padding: "1rem 0" }}>
              <p style={{ fontSize: "16px", marginBottom: "1.5rem" }}>
                Hello, {user.fullName || "there"}! 👋
              </p>

              <p
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginBottom: "1.5rem",
                }}
              >
                Thank you for joining RSU Learning Resource Center! Let's take a
                quick tour to help you get started.
              </p>
              <p style={{ fontSize: "12px", color: "#888" }}>
                ⏱️ Takes about 3-5 minutes • You can access tours anytime later
              </p>
            </div>
          </Modal.Content>

          <Modal.Actions style={{ justifyContent: "center" }}>
            <Button
              color="grey"
              onClick={handleSkipTour}
              size="medium"
              loading={isProcessing}
              disabled={isProcessing}
            >
              <Icon name="remove" /> Skip for Now
            </Button>
            <Button
              color="green"
              onClick={() => handleStartTour(TOUR_TYPES.ONBOARDING)}
              size="medium"
              loading={isProcessing}
              disabled={isProcessing}
            >
              <Icon name="play" /> Start Tour
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};

WelcomeModal.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    fullName: PropTypes.string,
    created_at: PropTypes.string,
    createdAt: PropTypes.string,
    login_count: PropTypes.number,
    profile: PropTypes.object,
  }),
  isAuthenticated: PropTypes.bool.isRequired,
  showWelcomeModal: PropTypes.bool,
  setShowWelcomeModal: PropTypes.func,
};

WelcomeModal.defaultProps = {
  user: null,
  showWelcomeModal: false,
  setShowWelcomeModal: null,
};

export default WelcomeModal;
